import styled, { css } from 'styled-components';

import {
  PLAYERS,
  MOBILE,
  ANIM,
  GRADIENTS,
  HOME,
  HOME_STICKY_HEAD,
  COLORS,
  FIXED_HEADER,
  HEADER_DESKTOP_PX,
  HEADER_MOBILE_PX,
  PLAYERS_FILTER_ANIM,
  REDUCED_MOTION,
  SPACE,
} from '../../theme/tokens';
import { SectionTitle, Text } from '../../components';

// The shared SectionTitle carries an 18px bottom margin for standalone section
// headings; here it sits inline in the centered Head row, where that margin would
// throw its vertical centering off (flex centres the margin box, lifting the title
// ~9px above the controls). Drop it so the title and the sort/search controls
// share one centre line. (`&&` outweighs SectionTitle's own single-class rule.)
export const HeadTitle = styled(SectionTitle)`
  flex-shrink: 0;

  && {
    margin-bottom: 0;
  }
`;

// The list panel — the #101012 surface that holds the cards AND the pagination
// footer (same container the mobile list sat in).
export const Container = styled.div`
  background: ${PLAYERS.containerBg};
  border-radius: 18px;
  padding: 8px;
`;

// Pagination footer, divided off from the cards by a hairline, inside the panel.
export const PagerFooter = styled.div`
  margin-top: 10px;
  padding: 14px 8px 6px;
  border-top: 1px solid ${PLAYERS.hairline};
  border-image: ${GRADIENTS.dividerStyledH} 1;
`;

// Shown only when the roster hit the fetch cap (a very large brand): a quiet caption
// so a partial list never silently reads as the whole roster.
export const RosterNote = styled(Text)`
  display: block;
  margin: 10px 8px 2px;
  font-size: 12px;
  line-height: 1.4;
  text-align: center;
  color: ${PLAYERS.pageMeta};
`;

// Zero-height marker riding the document flow at the header's top edge so
// `useStuckOnPin` can tell when the header has pinned. The negative margin cancels
// the Page column's flex gap (16px desktop / 14px mobile) so adding the sentinel
// never shifts the header. Only rendered in sticky mode.
export const StickySentinel = styled.div`
  height: 0;
  margin-bottom: -16px;

  ${FIXED_HEADER} {
    margin-bottom: -14px;
  }
`;

// Page header BLOCK: the title/controls row, with the collapsible filter tray
// stacked under it. Both live inside the ONE sticky element so the filters stay
// reachable while the roster scrolls — pinning the header but leaving the tray
// behind would hide the controls exactly when a long list needs them most.
//
// In `$sticky` mode it pins under the layout top bar and, once pinned (`$stuck`),
// morphs into a floating pill (opaque surface, rounded corners, soft shadow), with
// a solid fill bridging the band up to the top bar. The pill is drawn on
// negative-inset pseudos so the content never shifts as it appears; because they
// are `inset`-based they grow with the tray automatically.
export const Head = styled.div<{ $sticky?: boolean; $stuck?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0;

  ${(p) =>
    p.$sticky &&
    css`
      position: sticky;
      /* Under the top bar. The desktop offset omits the support-impersonation strip: the
         layout Root's top padding (--ows-imp-offset) already shifts this sticky element's
         scrollport down by the strip height, so re-adding it here would double-count it and
         drag the pinned pill the strip height too low. The mobile override keeps it (different
         regime). See BackHeader for the full rationale. */
      top: calc(${HEADER_DESKTOP_PX}px + var(--ows-imp-safe-top, env(safe-area-inset-top)));
      z-index: 5;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: calc(100% - 14px);
        height: calc(${HEADER_DESKTOP_PX}px + env(safe-area-inset-top));
        z-index: -2;
        background: ${COLORS.pageBg};
        opacity: 0;
        transition: opacity ${HOME_STICKY_HEAD.morphMs}ms ${HOME.ease};
        pointer-events: none;
      }

      &::after {
        content: '';
        position: absolute;
        /* Must stay inside PageShell's 16px column gutter: at -18px the pill ran 2px
           past the scroll area on each side (a horizontal scrollbar on the right, a
           clipped edge on the left). 12px leaves room for its border. */
        inset: -12px;
        z-index: -1;
        box-sizing: border-box;
        border-radius: ${HOME_STICKY_HEAD.pillRadius};
        background: ${HOME_STICKY_HEAD.pillBg};
        border: 1px solid ${HOME_STICKY_HEAD.pillBorder};
        box-shadow: ${HOME_STICKY_HEAD.pillShadow};
        opacity: 0;
        transform: translateY(-6px) scale(0.985);
        transform-origin: top center;
        transition: opacity ${HOME_STICKY_HEAD.morphMs}ms ${HOME.ease},
          transform ${HOME_STICKY_HEAD.morphMs}ms ${HOME.ease};
        pointer-events: none;
      }

      ${FIXED_HEADER} {
        /* Mobile keeps --ows-imp-offset: the fixed top bar overlays the viewport and the
           document is the scroller, so the strip height isn't already in the scrollport. */
        top: calc(${HEADER_MOBILE_PX}px + var(--ows-imp-safe-top, env(safe-area-inset-top)) + var(--ows-imp-offset, 0px));
        &::before {
          height: calc(${HEADER_MOBILE_PX}px + env(safe-area-inset-top));
        }
      }
    `}

  ${(p) =>
    p.$sticky &&
    p.$stuck &&
    css`
      &::before {
        opacity: 1;
      }
      &::after {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    `}

`;

// The title + controls row inside the header block. Wraps on narrower widths; on
// mobile it stays a single compact row (title + collapsed glyph buttons) so the
// pinned pill stays short even before the tray opens.
export const HeadRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px 16px;
  flex-wrap: wrap;

  ${MOBILE} {
    flex-wrap: nowrap;
    gap: 10px;
  }
`;

// Right-hand control group (sort + search). On mobile it collapses to a pair of
// compact 40px buttons (filter + search) that the search expands out of on tap.
export const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;

  ${MOBILE} {
    flex: 1;
    min-width: 0;
    flex-wrap: nowrap;
    gap: 8px;
  }
`;

// Sort pill — a leading glyph, the (borderless) native select, and a chevron, in
// the same surface/height as the search box so the two read as a pair.
export const SortBox = styled.label`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border-radius: 12px;
  background: ${PLAYERS.searchBg};
  border: 1px solid ${PLAYERS.searchBorder};
  cursor: pointer;
  transition: border-color ${ANIM.INTERACT_MS}ms ease;

  &:focus-within {
    border-color: ${PLAYERS.searchFocusBorder};
  }

  /* Mobile: collapse to a 40px icon button. The native <select> overlays it
     (below), so tapping the glyph opens the OS sort picker. */
  ${MOBILE} {
    flex: 0 0 40px;
    width: 40px;
    padding: 0;
    justify-content: center;
  }
`;

// The visible selected-sort label (the native <select> below is an invisible
// full-box overlay, so this span is just the face). Hidden on mobile, where the
// pill collapses to the filter glyph alone.
export const SortValue = styled.span`
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  color: ${PLAYERS.searchText};
  white-space: nowrap;
  pointer-events: none;

  ${MOBILE} {
    display: none;
  }
`;

// An invisible, full-box overlay at EVERY width, so a click anywhere on the pill
// (glyph, label or chevron) opens the native picker — not just the label text.
export const SortSelect = styled.select`
  appearance: none;
  -webkit-appearance: none;
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  border: none;
  outline: none;
  opacity: 0;
  /* Same fix as the filter tray's selects: solid themed colours so the OPEN list
     Windows/Linux paint from them is not white text on white. */
  background: ${PLAYERS.searchBg};
  color: ${PLAYERS.searchText};
  font-family: inherit;
  font-size: 14px;
  cursor: pointer;

  option {
    background: ${PLAYERS.searchBg};
    color: ${PLAYERS.searchText};
  }
`;

export const SortChevron = styled.span`
  display: inline-flex;
  align-items: center;
  pointer-events: none;
  color: ${PLAYERS.searchIcon};

  ${MOBILE} {
    display: none;
  }
`;

// Filters button — the SortBox pill as a real button (it toggles the tray below
// rather than opening a picker). Lights its border while the tray is open OR any
// filter is narrowing the list, so a collapsed tray never hides an active filter.
export const FilterToggle = styled.button<{ $active: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border-radius: 12px;
  background: ${PLAYERS.searchBg};
  border: 1px solid ${(p) => (p.$active ? PLAYERS.searchFocusBorder : PLAYERS.searchBorder)};
  color: ${PLAYERS.searchText};
  font-family: inherit;
  cursor: pointer;
  transition: border-color ${ANIM.INTERACT_MS}ms ease;

  /* Mobile: collapse to a 40px glyph button, matching the sort pill beside it. */
  ${MOBILE} {
    flex: 0 0 40px;
    width: 40px;
    padding: 0;
    justify-content: center;
    gap: 0;
  }
`;

export const FilterToggleLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${PLAYERS.searchText};
  white-space: nowrap;

  ${MOBILE} {
    display: none;
  }
`;

// Active-filter count. Inline on desktop; on mobile the label is gone, so it rides
// the glyph button's top-right corner instead of widening it.
export const FilterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 99px;
  background: ${PLAYERS.pageActiveBg};
  color: ${PLAYERS.pageActiveText};
  font-size: 11px;
  font-weight: 700;
  line-height: 1;

  ${MOBILE} {
    position: absolute;
    top: -6px;
    right: -6px;
  }
`;

// The collapsible filter tray, inside the sticky header block.
//
// Height animates via `grid-template-rows: 0fr → 1fr` rather than a `max-height`
// guess: the tray's height depends on how many source options the brand has and how
// far the fields wrap, so any fixed max-height would either clip the tray or make
// the close ease through dead space. `min-height: 0` on the child is what lets the
// grid row actually collapse.
//
// It stays MOUNTED when closed (height 0) so the transition can run both ways and
// the fields keep their focus/scroll state across a toggle.
export const FilterTray = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${(p) => (p.$open ? '1fr' : '0fr')};
  transition: grid-template-rows ${PLAYERS_FILTER_ANIM.TRAY_MS}ms ${PLAYERS_FILTER_ANIM.EASE};

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

// The clipping row. `overflow: hidden` is what makes the 0fr row visually collapse;
// the inner padding lives here (not on the tray) so it collapses with the height
// instead of holding the row open at its own size.
export const FilterTrayInner = styled.div<{ $open: boolean }>`
  min-height: 0;
  overflow: hidden;
  opacity: ${(p) => (p.$open ? 1 : 0)};
  transform: translateY(${(p) => (p.$open ? '0' : '-4px')});
  transition: opacity ${PLAYERS_FILTER_ANIM.CONTENT_MS}ms ${PLAYERS_FILTER_ANIM.EASE},
    transform ${PLAYERS_FILTER_ANIM.CONTENT_MS}ms ${PLAYERS_FILTER_ANIM.EASE};
  /* Closed, the tray must not catch clicks or hold a tab stop behind the header. */
  visibility: ${(p) => (p.$open ? 'visible' : 'hidden')};
  transition-property: opacity, transform, visibility;
  transition-duration: ${PLAYERS_FILTER_ANIM.CONTENT_MS}ms;

  ${REDUCED_MOTION} {
    transition: none;
  }
`;


// --- "Came from" filter ----------------------------------------------------------
// The removable chips over the cards when the page was opened filtered to one link's players or to a landing page's
// (`?campaign=` / `?landing_page=`), plus the note when a landing page filter could not be applied.
export const FromBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACE[8]};
  padding: ${SPACE[4]} ${SPACE[4]} ${SPACE[12]};
`;

export const FromFilterChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[4]};
  min-width: 0;
  max-width: 100%;
  padding: ${SPACE[4]} ${SPACE[4]} ${SPACE[4]} ${SPACE[12]};
  border-radius: 999px;
  border: 1px solid ${PLAYERS.pageBorder};
  background: ${PLAYERS.copyBg};
`;

export const FromFilterLabel = styled(Text)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 600;
  color: ${PLAYERS.email};
`;

export const FromFilterRemove = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 999px;
  color: ${PLAYERS.copyGlyph};
  background: transparent;
  cursor: pointer;
  transition: background ${ANIM.INTERACT_MS}ms ease;

  &:hover,
  &:focus-visible {
    background: ${PLAYERS.copyBgHover};
    outline: none;
  }
`;

export const FromNote = styled(Text)`
  flex-basis: 100%;
  font-size: 12px;
  line-height: 1.4;
  color: ${PLAYERS.pageMeta};
`;
